import classNames from 'classnames';
import React, { useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { groupCategoriesByGroup } from '../../../pages/Absences/GroupCategories';
import { CalendarEmployeeAbsenceCategory } from '../../AbsenceCalendar/types';
import { iconToCategoryTypeTheme } from '../../AbsenceCalendar/Utils';
import CategoryButton, { CategoriesGroupButton } from './CategoryButton';
import CategoryInfo from './CategoryInfo';
import { PureQueryOptions } from '@apollo/client';

type Props = {
  categories?: ReadonlyArray<CalendarEmployeeAbsenceCategory>;
  readOnly?: boolean;
  refetchQueries?: PureQueryOptions[];
};

type CategoryProps = {
  category: CalendarEmployeeAbsenceCategory;
  readOnly?: boolean;
  categories: ReadonlyArray<CalendarEmployeeAbsenceCategory>;
  refetchQueries?: PureQueryOptions[];
};

export const Category: React.FC<CategoryProps> = ({
  category,
  categories,
  readOnly,
  refetchQueries,
}) => {
  const { Name, flair__Icon__c, flair__Type__c } = category.category;
  const theme = iconToCategoryTypeTheme(flair__Icon__c);
  const availableAmount = category.allowancesSummary.availableAmount;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <OverlayTrigger
      trigger="click"
      placement={category.allowances.length > 3 ? 'bottom' : 'top'}
      rootClose={!isSidebarOpen}
      overlay={
        <Popover id={Name}>
          <CategoryInfo
            id={category.Id}
            name={Name}
            unlimited={category.flair__Unlimited__c}
            withoutBalance={category.category.flair__Without_Balance__c}
            allowances={category.allowances}
            theme={theme}
            readOnly={readOnly}
            categories={categories}
            type={flair__Type__c}
            onSidebarOpenChange={setIsSidebarOpen}
            refetchQueries={refetchQueries}
          />
        </Popover>
      }>
      <CategoryButton
        amount={availableAmount}
        unlimited={category.flair__Unlimited__c}
        withoutBalance={category.category.flair__Without_Balance__c}
        name={Name}
        theme={theme}
        type={flair__Type__c}
      />
    </OverlayTrigger>
  );
};

const Categories: React.FC<Props> = ({
  categories,
  readOnly = false,
  refetchQueries,
}) => {
  const groups = groupCategoriesByGroup<CalendarEmployeeAbsenceCategory>(
    categories || [],
  );

  const categoriesWithNoGroups = categories?.filter((c) => !c.category.group);

  return (
    <div
      className={classNames(
        'd-flex',
        'align-items-center',
        'flex-wrap',
        'gap-3',
      )}>
      {groups.map(
        (group) =>
          group.isGroup && (
            <div key={group.key}>
              <CategoriesGroupButton
                key={group.key}
                name={group.name || ''}
                categories={group.categories}
                readOnly={readOnly}
                refetchQueries={refetchQueries}
              />
            </div>
          ),
      )}
      {categoriesWithNoGroups?.map((category) => (
        <div key={category.Id}>
          <Category
            category={category}
            categories={categoriesWithNoGroups}
            readOnly={readOnly}
            refetchQueries={refetchQueries}
          />
        </div>
      ))}
    </div>
  );
};

export default Categories;
